import { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Link,
  Stack,
} from "@mui/material";
import { minHeightDvh } from "../utils/viewport";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await fetch("/auth/request-password-reset", {
        method: "POST",
        credentials: "omit",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
    } catch {
      // Swallow network errors — the UX is uniform on any response to
      // prevent email enumeration. A network failure is indistinguishable
      // from a rate-limit from the user's perspective.
    } finally {
      // Render success card regardless of status (200, 429, network error)
      // for uniform UX.
      setSubmitted(true);
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        ...minHeightDvh,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: (theme) =>
          theme.palette.mode === "light"
            ? "linear-gradient(160deg, #1d4ed8 0%, #6366f1 40%, #a78bfa 100%)"
            : "linear-gradient(160deg, #0c1a3d 0%, #0f0b2e 40%, #1e0a4a 100%)",
        px: 2,
      }}
    >
      <Card sx={{ width: "100%", maxWidth: 440, borderRadius: 4 }}>
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          {/* Gradient logo icon */}
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: 1,
              background: "linear-gradient(135deg, #1d4ed8, #a78bfa)",
              mx: "auto",
              mb: 1.5,
            }}
          />

          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            align="center"
            fontWeight={700}
          >
            PDFGlyph
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            align="center"
            sx={{ mb: 3 }}
          >
            Reset your password
          </Typography>

          {submitted ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              Check your inbox. If an account exists for that email, we've sent
              a password reset link.
            </Alert>
          ) : (
            <form onSubmit={handleSubmit}>
              <Stack spacing={2}>
                <TextField
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  fullWidth
                  autoComplete="email"
                  autoFocus
                />
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  fullWidth
                  disabled={loading}
                  sx={{
                    borderRadius: 24,
                    background: "linear-gradient(135deg, #1d4ed8, #a78bfa)",
                    color: "white",
                    "&:hover": {
                      background: "linear-gradient(135deg, #1e40af, #7c3aed)",
                    },
                  }}
                >
                  {loading ? "Sending..." : "Send reset link"}
                </Button>
              </Stack>
            </form>
          )}

          <Typography variant="body2" align="center" sx={{ mt: 3 }}>
            Remembered it?{" "}
            <Link component={RouterLink} to="/login">
              Sign in
            </Link>
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}
